import { api } from './api';

const CACHE_KEY = 'currencies';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

class CurrencyService {
  constructor() {
    this.cache = [];
    this.cacheTimestamp = null;
  }

  /**
   * Get all currencies
   */
  async getAllCurrencies(forceRefresh = false) {
    if (!forceRefresh && Array.isArray(this.cache) && this.cache.length > 0 && this.cacheTimestamp && 
        (Date.now() - this.cacheTimestamp < CACHE_DURATION)) {
      return this.cache;
    }

    try {
      const response = await api.get('/currencies');
      
      // The API returns {success: true, data: [...]} so we need response.data.data
      // But let's check both possible structures
      let currencies;
      if (response.data?.data) {
        currencies = response.data.data;
      } else if (Array.isArray(response.data)) {
        currencies = response.data;
      } else {
        currencies = [];
      }
      
      this.cache = Array.isArray(currencies) ? currencies : [];
      this.cacheTimestamp = Date.now();
      return this.cache;
    } catch (error) {
      console.error('Error fetching currencies:', error);
      // Return empty array on error instead of throwing
      return [];
    }
  }

  /**
   * Get active currencies only
   */
  async getActiveCurrencies(forceRefresh = false) {
    try {
      const response = await api.get('/currencies?active=true');
      
      // Handle both possible response structures
      let currencies;
      if (response.data?.data) {
        currencies = response.data.data;
      } else if (Array.isArray(response.data)) {
        currencies = response.data;
      } else {
        currencies = [];
      }
      
      return Array.isArray(currencies) ? currencies : [];
    } catch (error) {
      console.error('Error fetching active currencies:', error);
      return [];
    }
  }

  /**
   * Get currency options for select fields
   */
  async getCurrencyOptions() {
    try {
      const response = await api.get('/currencies/options');
      
      console.log('🔍 Currency API Raw Response:', response);
      console.log('🔍 Currency API Response Data:', response.data);
      
      // Handle different response structures
      let options;
      if (response.data?.success && response.data?.data) {
        // Handle {success: true, data: [...]} structure
        options = response.data.data;
        console.log('📦 Extracted options from response.data.data:', options);
      } else if (response.data?.data) {
        // Handle {data: [...]} structure
        options = response.data.data;
        console.log('📦 Extracted options from response.data.data (fallback):', options);
      } else if (Array.isArray(response.data)) {
        // Handle direct array response
        options = response.data;
        console.log('📦 Using response.data directly as array:', options);
      } else {
        console.warn('⚠️ Unrecognized response structure:', response.data);
        options = [];
      }
      
      // Ensure we return a valid array with proper structure
      const validOptions = Array.isArray(options) ? options.filter(option => {
        const isValid = option && 
          (option.value !== undefined && option.value !== null) && 
          (option.label !== undefined && option.label !== null);
        if (!isValid) {
          console.warn('⚠️ Invalid currency option found:', option);
        }
        return isValid;
      }) : [];
      
      console.log('✅ Final valid currency options:', validOptions);
      
      // If we got valid options, return them
      if (validOptions.length > 0) {
        return validOptions;
      }
      
      console.warn('⚠️ No valid currency options found, returning empty array');
      // Return empty array if no valid options
      return [];
    } catch (error) {
      console.error('❌ Error fetching currency options:', error);
      // Return empty array if API fails
      return [];
    }
  }

  /**
   * Get single currency by ID
   */
  async getCurrency(id) {
    try {
      const response = await api.get(`/currencies/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching currency:', error);
      throw error;
    }
  }

  /**
   * Get currency by code
   */
  async getCurrencyByCode(code) {
    try {
      const response = await api.get(`/currencies/code/${code}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching currency by code:', error);
      throw error;
    }
  }

  /**
   * Create new currency
   */
  async createCurrency(currencyData) {
    try {
      const response = await api.post('/currencies', currencyData);
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('Error creating currency:', error);
      throw error;
    }
  }

  /**
   * Update currency
   */
  async updateCurrency(id, updates) {
    try {
      const response = await api.put(`/currencies/${id}`, updates);
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('Error updating currency:', error);
      throw error;
    }
  }

  /**
   * Delete/deactivate currency
   */
  async deleteCurrency(id) {
    try {
      const response = await api.delete(`/currencies/${id}`);
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('Error deleting currency:', error);
      throw error;
    }
  }

  /**
   * Restore/activate currency
   */
  async restoreCurrency(id) {
    try {
      const response = await api.put(`/currencies/${id}`, { isActive: true });
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('Error restoring currency:', error);
      throw error;
    }
  }

  /**
   * Set base currency
   */
  async setBaseCurrency(id) {
    try {
      const response = await api.put(`/currencies/${id}/set-base`);
      
      // Handle both possible response structures
      let result;
      if (response.data?.data || response.data?.success) {
        result = response.data;
      } else {
        result = response;
      }
      
      this.clearCache();
      return result;
    } catch (error) {
      console.error('Error setting base currency:', error);
      throw error;
    }
  }

  /**
   * Update exchange rates
   */
  async updateExchangeRates(rates) {
    try {
      const response = await api.post('/currencies/exchange-rates', { rates });
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('Error updating exchange rates:', error);
      throw error;
    }
  }

  /**
   * Convert currency
   */
  async convertCurrency(amount, fromCode, toCode) {
    try {
      const response = await api.post('/currencies/convert', {
        amount,
        from: fromCode,
        to: toCode
      });
      return response.data.data;
    } catch (error) {
      console.error('Error converting currency:', error);
      throw error;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount, currencyCode, locale = 'en-US') {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      // Fallback for unsupported currencies
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Get currency symbol from code
   */
  async getCurrencySymbol(code) {
    try {
      const currency = await this.getCurrencyByCode(code);
      return currency.symbol;
    } catch (error) {
      // Fallback symbols
      const fallbackSymbols = {
        USD: '$',
        EGP: 'E£',
        SAR: '﷼',
        QAR: 'ر.ق',
        AED: 'د.إ',
        KWD: 'د.ك',
        BHD: 'د.ب',
        OMR: 'ر.ع.',
        EUR: '€',
        GBP: '£'
      };
      return fallbackSymbols[code] || code;
    }
  }
}

export default new CurrencyService();